"use client";

import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

export type CcShowcasePillItem = {
  id: string;
  label: ReactNode;
  tabId?: string;
  controlsId?: string;
};

type CcShowcasePillToggleProps = {
  items: readonly CcShowcasePillItem[];
  activeId: string;
  onSelect: (id: string) => void;
  ariaLabel: string;
  /** Före/Efter uses `toggle`; value-demo tabs use `tabs`. */
  variant?: "toggle" | "tabs";
  className?: string;
  scrollClassName?: string;
  optionClassName?: string;
};

/**
 * Compact segmented pills with a sliding brand indicator — same interaction as
 * the AI showcase Före/Efter control (`cc-ai-showcase__toggle`).
 */
export function CcShowcasePillToggle({
  items,
  activeId,
  onSelect,
  ariaLabel,
  variant = "toggle",
  className,
  scrollClassName,
  optionClassName,
}: CcShowcasePillToggleProps) {
  const optionRefs = useRef(new Map<string, HTMLButtonElement>());
  const [indicator, setIndicator] = useState({ left: 0, width: 0, ready: false });

  const syncIndicator = useCallback(() => {
    const activeButton = optionRefs.current.get(activeId);
    if (!activeButton) {
      return;
    }
    setIndicator({
      left: activeButton.offsetLeft,
      width: activeButton.offsetWidth,
      ready: true,
    });
  }, [activeId]);

  useLayoutEffect(() => {
    syncIndicator();
    window.addEventListener("resize", syncIndicator);
    return () => window.removeEventListener("resize", syncIndicator);
  }, [syncIndicator, items]);

  const trackClassName = ["cc-ai-showcase__toggle", className].filter(Boolean).join(" ");
  const optionClasses = ["cc-ai-showcase__toggle-option", optionClassName]
    .filter(Boolean)
    .join(" ");
  const scrollClasses = scrollClassName ? scrollClassName : undefined;

  const track = (
    <div
      className={trackClassName}
      role={variant === "tabs" ? "tablist" : "group"}
      aria-label={ariaLabel}
    >
      <span
        className="cc-ai-showcase__toggle-indicator"
        aria-hidden="true"
        style={
          {
            "--cc-showcase-pill-left": `${indicator.left}px`,
            "--cc-showcase-pill-width": `${indicator.width}px`,
            opacity: indicator.ready ? 1 : 0,
          } as CSSProperties
        }
      />
      {items.map((item) => {
        const isActive = item.id === activeId;
        const shared = {
          ref: (node: HTMLButtonElement | null) => {
            if (node) optionRefs.current.set(item.id, node);
            else optionRefs.current.delete(item.id);
          },
          type: "button" as const,
          className: optionClasses,
          onClick: () => onSelect(item.id),
        };

        if (variant === "tabs") {
          return (
            <button
              key={item.id}
              {...shared}
              role="tab"
              id={item.tabId ?? item.id}
              aria-selected={isActive}
              aria-controls={item.controlsId}
            >
              {item.label}
            </button>
          );
        }

        return (
          <button key={item.id} {...shared} aria-pressed={isActive}>
            {item.label}
          </button>
        );
      })}
    </div>
  );

  if (!scrollClasses) {
    return track;
  }

  return <div className={scrollClasses}>{track}</div>;
}
